import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Globe } from "lucide-react";

// Each language is named in its own script, with `lang`/`dir` so screen readers
// pronounce it correctly. `short` is the compact label used on narrow screens.
const languageOptions = [
  { value: "en", label: "English", short: "EN", dir: "ltr" },
  { value: "ar", label: "العربية", short: "ع", dir: "rtl" },
  { value: "zh-Hans", label: "简体中文", short: "中", dir: "ltr" },
];

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const optionRefs = useRef([]);
  const activeIndexRef = useRef(0);
  const currentLang = i18n.language;
  const currentOption = useMemo(
    () => languageOptions.find((option) => option.value === currentLang) || languageOptions[0],
    [currentLang]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const focusIndex =
      activeIndexRef.current >= 0 &&
      activeIndexRef.current < languageOptions.length
        ? activeIndexRef.current
        : 0;
    activeIndexRef.current = focusIndex;
    const focusTarget = optionRefs.current[focusIndex] || optionRefs.current[0];
    focusTarget?.focus?.();
  }, [isOpen, currentLang]);

  const applyLanguage = (next) => {
    window.dispatchEvent(
      new CustomEvent("tarh:locale-change-start", {
        detail: { locale: next },
      })
    );
    localStorage.setItem("lang", next);
    i18n.changeLanguage(next);
    document.documentElement.setAttribute("dir", next === "ar" ? "rtl" : "ltr");
    document.documentElement.lang = next;
    setIsOpen(false);
    buttonRef.current?.focus?.();
  };

  const openMenuAtIndex = (index) => {
    const safeIndex = Math.max(0, Math.min(index, languageOptions.length - 1));
    activeIndexRef.current = safeIndex;
    setIsOpen(true);
  };

  const handleButtonClick = () => {
    const selectedIndex = languageOptions.findIndex(
      (option) => option.value === currentLang
    );
    const nextIndex = selectedIndex >= 0 ? selectedIndex : 0;
    activeIndexRef.current = nextIndex;
    setIsOpen((prev) => !prev);
  };

  const handleButtonKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      const selectedIndex = languageOptions.findIndex(
        (option) => option.value === currentLang
      );
      openMenuAtIndex(selectedIndex >= 0 ? selectedIndex : 0);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      openMenuAtIndex(languageOptions.length - 1);
    }
  };

  const handleMenuKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      buttonRef.current?.focus?.();
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const direction = event.key === "ArrowDown" ? 1 : -1;
      const currentIndex = activeIndexRef.current;
      const nextIndex =
        (currentIndex + direction + languageOptions.length) %
        languageOptions.length;
      activeIndexRef.current = nextIndex;
      optionRefs.current[nextIndex]?.focus?.();
    }
  };

  return (
    <div ref={containerRef} className="language-switcher-wrapper relative lg:me-2">
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        onKeyDown={handleButtonKeyDown}
        className={`group inline-flex h-10 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-(--primary-green)/40 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 ${
          isOpen ? "bg-slate-100 dark:bg-slate-700" : ""
        }`}
        type="button"
        aria-haspopup="menu"
        aria-label={`${t("language")}: ${currentOption.label}`}
        aria-expanded={isOpen}
        aria-controls="language-switcher-menu"
      >
        <Globe
          size={16}
          strokeWidth={2}
          className={`shrink-0 transition-colors ${isOpen ? "text-(--primary-green)" : "text-slate-500 dark:text-slate-300"}`}
          aria-hidden="true"
        />
        <span lang={currentOption.value} dir={currentOption.dir} className="leading-none">
          <span className="hidden sm:inline">{currentOption.label}</span>
          <span className="sm:hidden">{currentOption.short}</span>
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2.25}
          className={`shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180 text-(--primary-green)" : "text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200"
          }`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          id="language-switcher-menu"
          role="menu"
          aria-label={t("language")}
          onKeyDown={handleMenuKeyDown}
          className="absolute end-0 top-full z-50 mt-2 min-w-48 origin-top-right rtl:origin-top-left animate-menu-in rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl shadow-slate-900/10 motion-reduce:animate-none dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="px-2.5 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
            {t("language")}
          </div>
          {languageOptions.map((option, index) => {
            const isSelected = currentLang === option.value;
            return (
              <button
                key={option.value}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={isSelected}
                onFocus={() => {
                  activeIndexRef.current = index;
                }}
                onClick={() => applyLanguage(option.value)}
                className={`flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-start text-sm transition-colors duration-150 focus:outline-none ${
                  isSelected
                    ? "bg-emerald-50 font-semibold text-(--primary-green) dark:bg-emerald-500/10 dark:text-emerald-400"
                    : "font-medium text-slate-700 hover:bg-slate-100 focus-visible:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700 dark:focus-visible:bg-slate-700"
                }`}
              >
                <span lang={option.value} dir={option.dir} className="flex-1">
                  {option.label}
                </span>
                {isSelected && <Check size={16} strokeWidth={2.25} className="shrink-0" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
